import { Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Typography } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import type { IncidentFormField, IncidentFormSchema } from '../types/incident';

const { Text } = Typography;
const { TextArea } = Input;

type ProductFormRendererProps = {
  schema?: IncidentFormSchema | null;
  namePrefix?: NamePath;
  disabled?: boolean;
};

const fieldName = (prefix: NamePath | undefined, field: IncidentFormField): NamePath => {
  const base = Array.isArray(prefix) ? prefix : prefix ? [prefix] : [];
  return [...base, field.id];
};

const ProductFormRenderer = ({ schema, namePrefix = ['product_form_response'], disabled = false }: ProductFormRendererProps) => {
  const fields = schema?.fields || [];
  if (fields.length === 0) {
    return <Text type="secondary">追加項目はありません。</Text>;
  }

  return (
    <Row gutter={16}>
      {fields.map((field) => {
        if (field.type === 'section') {
          return (
            <Col xs={24} key={field.id}>
              <Divider orientation="left">{field.label}</Divider>
              {field.description ? <p className="productFormFieldHelp">{field.description}</p> : null}
            </Col>
          );
        }

        const rules = field.required ? [{ required: true, message: `${field.label}を入力してください` }] : undefined;
        const help = field.description ? <span className="productFormFieldHelp">{field.description}</span> : undefined;
        const label = field.required ? `${field.label} *` : field.label;

        let control = <Input disabled={disabled} placeholder={field.placeholder || undefined} />;
        if (field.type === 'textarea') {
          control = <TextArea disabled={disabled} rows={3} placeholder={field.placeholder || undefined} />;
        } else if (field.type === 'number') {
          control = <InputNumber className="fullWidthControl" disabled={disabled} addonAfter={field.unit || undefined} />;
        } else if (field.type === 'date') {
          control = <DatePicker className="fullWidthControl" disabled={disabled} />;
        } else if (field.type === 'select') {
          control = (
            <Select
              allowClear
              disabled={disabled}
              options={field.options.map((option) => ({ value: option, label: option }))}
              placeholder={field.placeholder || undefined}
            />
          );
        } else if (field.type === 'multi_select') {
          control = (
            <Select
              allowClear
              mode="multiple"
              disabled={disabled}
              options={field.options.map((option) => ({ value: option, label: option }))}
              placeholder={field.placeholder || undefined}
            />
          );
        } else if (field.type === 'checkbox') {
          return (
            <Col xs={24} md={12} key={field.id}>
              <Form.Item name={fieldName(namePrefix, field)} valuePropName="checked" help={help}>
                <Checkbox disabled={disabled}>{label}</Checkbox>
              </Form.Item>
            </Col>
          );
        }

        return (
          <Col xs={24} md={field.type === 'textarea' ? 24 : 12} key={field.id}>
            <Form.Item name={fieldName(namePrefix, field)} label={label} rules={rules} help={help}>
              {control}
            </Form.Item>
          </Col>
        );
      })}
    </Row>
  );
};

export default ProductFormRenderer;
